import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SEO, { generateCatalogSchema, generateBreadcrumbSchema } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Truck, Search, Filter, MapPin, Fuel, Ruler, Package, Calendar, X, Loader2, Gauge } from "lucide-react";
import axios from "axios";
import { formatCurrency } from "../lib/utils";

// Use relative URL if BACKEND_URL is not set (for production builds like Railway)
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Vehicle types for filter
const VEHICLE_TYPES = [
  { id: "piccolo", label: "Piccolo" },
  { id: "medio", label: "Medio" },
  { id: "elettrico", label: "Elettrico" },
  { id: "grande", label: "Grande" },
  { id: "autocarro", label: "Autocarro" },
  { id: "isotermico", label: "Isotermico" },
  { id: "sponda_idraulica", label: "Sponda Idraulica" },
  { id: "pickup", label: "Pickup" },
];

// KM tiers for filter
const KM_TIERS = [
  { id: "100", label: "100 km/giorno" },
  { id: "200", label: "200 km/giorno" },
  { id: "300", label: "300 km/giorno" },
  { id: "400", label: "400 km/giorno" },
  { id: "500", label: "500 km/giorno" },
  { id: "illimitati", label: "Km Illimitati" },
];

export default function CatalogPage() {
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyPricing, setCompanyPricing] = useState({}); // Cache dei tariffari per company
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    company: "",
    engineType: "",
    vehicleType: "",
    kmTier: "",
    city: "",
    province: "",
    maxPrice: 500
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  // Extract unique cities and provinces from companies
  const [cities, setCities] = useState([]);
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Build query params for backend filtering
      let url = `${API}/vehicles?available_only=true`;
      if (filters.vehicleType) {
        url += `&vehicle_type=${filters.vehicleType}`;
      }
      if (filters.kmTier) {
        url += `&km_tier=${filters.kmTier}`;
      }
      
      const [vehiclesRes, companiesRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/companies`)
      ]);
      setVehicles(vehiclesRes.data);
      setCompanies(companiesRes.data);

      // Fetch pricing for each company
      const uniqueCompanyIds = [...new Set(vehiclesRes.data.map(v => v.company_id))];
      const pricingPromises = uniqueCompanyIds.map(async (companyId) => {
        try {
          const res = await axios.get(`${API}/companies/${companyId}/pricing`);
          return { companyId, pricing: res.data };
        } catch {
          return { companyId, pricing: null };
        }
      });
      
      const pricingResults = await Promise.all(pricingPromises);
      const pricingMap = {};
      pricingResults.forEach(({ companyId, pricing }) => {
        pricingMap[companyId] = pricing;
      });
      setCompanyPricing(pricingMap);

      // Extract unique cities and provinces
      const uniqueCities = [...new Set(companiesRes.data.map(c => c.city).filter(Boolean))].sort();
      const uniqueProvinces = [...new Set(companiesRes.data.map(c => c.province).filter(Boolean))].sort();
      setCities(uniqueCities);
      setProvinces(uniqueProvinces);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Refetch when filter changes
  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [filters.vehicleType, filters.kmTier]);

  const getCompany = (companyId) => {
    return companies.find(c => c.company_id === companyId);
  };

  // Get minimum price from tariff for a vehicle
  const getMinPrice = (vehicle) => {
    const pricing = companyPricing[vehicle.company_id];
    if (!pricing || !pricing.pricing) return null;
    
    const vehicleType = vehicle.vehicle_type || "medio";
    const vtPricing = pricing.pricing.find(p => p.vehicle_type === vehicleType);
    
    if (!vtPricing || !vtPricing.tiers || vtPricing.tiers.length === 0) return null;
    
    // Find minimum price from all tiers
    const prices = vtPricing.tiers.map(t => t.price).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const filteredVehicles = vehicles.filter((v) => {
    const company = getCompany(v.company_id);
    
    // Search by brand/model
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!v.brand.toLowerCase().includes(searchLower) && 
          !v.model.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    // Filter by company
    if (filters.company && v.company_id !== filters.company) return false;
    
    // Filter by engine type
    if (filters.engineType && v.engine_type !== filters.engineType) return false;
    
    // Filter by city
    if (filters.city && company?.city !== filters.city) return false;
    
    // Filter by province
    if (filters.province && company?.province !== filters.province) return false;
    
    // Filter by max price (use tariff price if available)
    const minPrice = getMinPrice(v);
    if (minPrice && minPrice > filters.maxPrice) return false;
    
    return true;
  });

  const clearFilters = () => {
    setFilters({
      search: "",
      company: "",
      engineType: "",
      vehicleType: "",
      kmTier: "",
      city: "",
      province: "",
      maxPrice: 500
    });
  };

  const hasActiveFilters = filters.search || filters.company || filters.engineType || 
                           filters.vehicleType || filters.kmTier ||
                           filters.city || filters.province || filters.maxPrice < 500;

  const activeFilterCount = [
    filters.search, 
    filters.company, 
    filters.engineType,
    filters.vehicleType,
    filters.kmTier,
    filters.city, 
    filters.province, 
    filters.maxPrice < 500
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <SEO
        title="Catalogo Furgoni - Trova il Veicolo Perfetto"
        description="Sfoglia il nostro catalogo di furgoni disponibili per il noleggio. Filtra per città, prezzo, motorizzazione e trova il furgone perfetto per le tue esigenze."
        keywords="catalogo furgoni, furgoni disponibili, noleggio furgoni, furgoni per trasloco, furgoni commerciali"
        url="/catalogo"
        schema={[
          generateCatalogSchema(filteredVehicles),
          generateBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Catalogo Furgoni" }
          ])
        ]}
      />
      <Header />

      {/* Page Header */}
      <div className="bg-foreground text-background py-12">
        <div className="container mx-auto px-4">
          <h1 
            className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-2"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Catalogo <span className="text-primary">Veicoli</span>
          </h1>
          <p className="text-muted-foreground">
            Trova il furgone perfetto per le tue esigenze nella tua zona
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="bg-background border border-border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per marca o modello..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="catalog-search"
              />
            </div>
            
            {/* Quick Zone Filter */}
            <div className="flex gap-2">
              <Select 
                value={filters.city || "all"} 
                onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? "" : v })}
              >
                <SelectTrigger className="w-40" data-testid="quick-city-filter">
                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Città" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le città</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtri
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-primary text-primary-foreground">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Province Filter */}
                <div>
                  <label className="input-label">Provincia</label>
                  <Select
                    value={filters.province || "all"}
                    onValueChange={(v) => setFilters({ ...filters, province: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-province">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le province</SelectItem>
                      {provinces.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter (in extended) */}
                <div>
                  <label className="input-label">Città</label>
                  <Select
                    value={filters.city || "all"}
                    onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-city">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le città</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Company Filter */}
                <div>
                  <label className="input-label">Azienda</label>
                  <Select
                    value={filters.company || "all"}
                    onValueChange={(v) => setFilters({ ...filters, company: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-company">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le aziende</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.company_id} value={c.company_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Engine Type Filter */}
                <div>
                  <label className="input-label">Motorizzazione</label>
                  <Select
                    value={filters.engineType || "all"}
                    onValueChange={(v) => setFilters({ ...filters, engineType: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-engine">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Benzina">Benzina</SelectItem>
                      <SelectItem value="Elettrico">Elettrico</SelectItem>
                      <SelectItem value="Ibrido">Ibrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Vehicle Type Filter */}
                <div>
                  <label className="input-label">Tipologia Veicolo</label>
                  <Select
                    value={filters.vehicleType || "all"}
                    onValueChange={(v) => setFilters({ ...filters, vehicleType: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-vehicle-type">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le tipologie</SelectItem>
                      {VEHICLE_TYPES.map(vt => (
                        <SelectItem key={vt.id} value={vt.id}>{vt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* KM Tier Filter */}
                <div>
                  <label className="input-label">Tariffa Km</label>
                  <Select
                    value={filters.kmTier || "all"}
                    onValueChange={(v) => setFilters({ ...filters, kmTier: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="filter-km-tier">
                      <SelectValue placeholder="Tutte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le tariffe</SelectItem>
                      {KM_TIERS.map(tier => (
                        <SelectItem key={tier.id} value={tier.id}>{tier.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Filter */}
                <div>
                  <label className="input-label">Prezzo max: {formatCurrency(filters.maxPrice)}/giorno</label>
                  <Slider
                    value={[filters.maxPrice]}
                    onValueChange={(value) => setFilters({ ...filters, maxPrice: value[0] })}
                    max={500}
                    min={20}
                    step={10}
                    className="mt-4"
                    data-testid="filter-price"
                  />
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  data-testid="clear-filters"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancella Filtri
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {filters.vehicleType && (
              <Badge variant="secondary" className="py-1 px-3">
                <Truck className="h-3 w-3 mr-1" />
                {VEHICLE_TYPES.find(vt => vt.id === filters.vehicleType)?.label}
                <button onClick={() => setFilters({ ...filters, vehicleType: "" })} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.kmTier && (
              <Badge variant="secondary" className="py-1 px-3">
                <Gauge className="h-3 w-3 mr-1" />
                {KM_TIERS.find(t => t.id === filters.kmTier)?.label}
                <button onClick={() => setFilters({ ...filters, kmTier: "" })} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.city && (
              <Badge variant="secondary" className="py-1 px-3">
                <MapPin className="h-3 w-3 mr-1" />
                {filters.city}
                <button onClick={() => setFilters({ ...filters, city: "" })} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.province && (
              <Badge variant="secondary" className="py-1 px-3">
                Provincia: {filters.province}
                <button onClick={() => setFilters({ ...filters, province: "" })} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.engineType && (
              <Badge variant="secondary" className="py-1 px-3">
                {filters.engineType}
                <button onClick={() => setFilters({ ...filters, engineType: "" })} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {filters.maxPrice < 500 && (
              <Badge variant="secondary" className="py-1 px-3">
                Max {formatCurrency(filters.maxPrice)}/giorno
                <button onClick={() => setFilters({ ...filters, maxPrice: 500 })} className="ml-2">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Results Count */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">
            {filteredVehicles.length} veicoli trovati
            {(filters.city || filters.province) && (
              <span className="ml-1">
                {filters.city && `a ${filters.city}`}
                {filters.province && !filters.city && `in provincia di ${filters.province}`}
              </span>
            )}
          </p>
        </div>

        {/* Vehicle Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-20 bg-background border border-border">
            <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-bold text-lg mb-2">Nessun veicolo trovato</h3>
            <p className="text-muted-foreground text-sm mb-4">
              {filters.city || filters.province 
                ? "Prova a cercare in un'altra zona o modifica i filtri"
                : "Prova a modificare i filtri di ricerca"
              }
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Cancella Filtri
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles.map((vehicle) => {
              const company = getCompany(vehicle.company_id);
              return (
                <div
                  key={vehicle.vehicle_id}
                  className="card-vehicle bg-background cursor-pointer"
                  onClick={() => navigate(`/veicolo/${vehicle.vehicle_id}`)}
                  data-testid={`vehicle-card-${vehicle.vehicle_id}`}
                >
                  {/* Image */}
                  <div className="aspect-video bg-muted relative overflow-hidden">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <img
                        src={vehicle.images[0].startsWith('http') ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Truck className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground">
                      {vehicle.engine_type}
                    </Badge>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {vehicle.brand} {vehicle.model}
                        </h3>
                        <p className="text-sm text-muted-foreground">{company?.name || "Azienda"}</p>
                        {/* Location */}
                        {company && (
                          <p className="text-sm text-primary flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3" />
                            {company.city}, {company.province}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Specs */}
                    <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Ruler className="h-4 w-4" />
                        <span>{vehicle.length_m}m x {vehicle.height_m}m</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Package className="h-4 w-4" />
                        <span>{vehicle.cargo_volume_m3}m³</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Fuel className="h-4 w-4" />
                        <span>{vehicle.engine_type}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{vehicle.year}</span>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="flex items-end justify-between pt-3 border-t border-border">
                      <div>
                        {(() => {
                          const minPrice = getMinPrice(vehicle);
                          if (minPrice) {
                            return (
                              <>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">A partire da</p>
                                <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                  {formatCurrency(minPrice)}
                                  <span className="text-sm font-normal text-muted-foreground">/giorno</span>
                                </p>
                              </>
                            );
                          } else {
                            return (
                              <p className="text-sm text-muted-foreground">
                                Prezzo su richiesta
                              </p>
                            );
                          }
                        })()}
                      </div>
                      <Button className="btn-primary py-2 px-4" data-testid={`view-vehicle-${vehicle.vehicle_id}`}>
                        Dettagli
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
